import { FC, useContext, useEffect, useMemo, useState } from 'react';
import { IEventMetadata, ISourceData, ISourceDataConfiguration, ISourceDataConfigurationEvent, ISourceDataEvent, ITrialFormMetadata, SourceDataPriority } from '@/api/dtos';
import { BasicAlert, CollapsiblePanel, DefaultCollapsiblePanelComponent, ICollapsiblePanelComponentProps, Markdown } from '@ngt/forms';
import { alpha, Box, Button, Chip, Divider, styled, Tooltip, Typography } from '@mui/material';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCalendarDays, faEclipseAlt, faRepeat } from '@fortawesome/duotone-light-svg-icons';
import { faCheckCircle, faExclamationCircle, faExclamationTriangle, faInfoCircle } from '@fortawesome/pro-light-svg-icons';
import { faExclamationTriangle as faExclamationTriangleSolid, faTriangle } from '@fortawesome/pro-solid-svg-icons';
import { ISourceDataFormEventComponentProps } from '@/utilities/forms/SourceDataFormProcessor';

const Title = styled(Typography)(({ theme }) => ({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-start',
    padding: theme.spacing(4, 2, 0, 2)
}));

const IconContainer = styled(Box)(({ theme }) => ({
    marginRight: theme.spacing(2)
}));

const ExtraContainer = styled(Box)(({ theme }) => ({
    display: 'flex',
    marginLeft: 'auto',
    alignItems: 'center'
}));

const PriorityContainer = styled(Box)(({ theme }) => ({
    marginRight: theme.spacing(2)
}));

const TitleChip = styled(Chip)(({ theme }) => ({
    marginLeft: 'auto'
}));

const RepeatChip = styled(Chip)(({ theme }) => ({
}));

const Content = styled(Box)(({ theme }) => ({
}));

const SourceDataFormEventTitled: FC<ISourceDataFormEventComponentProps> = ({
    eventIdentifier,
    eventLabel,
    eventMetadata,
    eventSourceData,
    eventConfiguration,
    eventRepeat,
    settings,
    children
}) => {
    const hasEvent = !!eventSourceData;

    if (!hasEvent && settings?.hideEvents) {
        return null;
    }

    return (
        <>
            <a id={eventIdentifier} style={{position: 'relative', top: -64}} />
            <Title variant="h2">
                <IconContainer>
                    <FontAwesomeIcon icon={faCalendarDays} />
                </IconContainer>
                {eventLabel}
                <ExtraContainer>
                    {
                        eventSourceData?.priority === SourceDataPriority.Low && (
                            <Tooltip title="Priority: Low">
                                <PriorityContainer sx={{color: 'success.main', transform: 'rotate(180deg)'}}>
                                    <FontAwesomeIcon icon={faTriangle} />
                                </PriorityContainer>
                            </Tooltip>
                        )
                    }
                    {
                        eventSourceData?.priority === SourceDataPriority.High && (
                            <Tooltip title="Priority: High">
                                <PriorityContainer sx={{color: 'warning.main'}}>
                                    <FontAwesomeIcon icon={faTriangle} />
                                </PriorityContainer>
                            </Tooltip>
                        )
                    }
                    {
                        eventSourceData?.priority === SourceDataPriority.Critical && (
                            <Tooltip title="Priority: Critical">
                                <PriorityContainer sx={{color: 'error.main'}}>
                                    <FontAwesomeIcon icon={faExclamationTriangleSolid} />
                                </PriorityContainer>
                            </Tooltip>
                        )
                    }
                    {
                        eventMetadata?.repeating && (
                            <Tooltip title="Repeat">
                                <RepeatChip
                                    icon={<FontAwesomeIcon icon={faRepeat} />}
                                    label={eventRepeat}
                                />
                            </Tooltip>
                        )
                    }
                </ExtraContainer>
            </Title>
            <Content>
                {
                    !hasEvent && (
                        <BasicAlert
                            title="No Recorded Event"
                            description="The event has not yet been recorded in OpenClinica."
                            severity="info"
                            icon={faInfoCircle}
                        />
                    )
                }
                {
                    hasEvent && (
                        <>
                            { 
                                !!eventConfiguration?.alertInformation && (
                                    <BasicAlert
                                        description={<Markdown.Preview>{eventConfiguration.alertInformation}</Markdown.Preview>}
                                        severity="info"
                                        icon={faInfoCircle}
                                    />
                                )
                            }
                            { 
                                !!eventConfiguration?.alertWarning && (
                                    <BasicAlert
                                        description={<Markdown.Preview>{eventConfiguration.alertWarning}</Markdown.Preview>}
                                        severity="warning"
                                        icon={faExclamationCircle}
                                    />
                                )
                            }
                            { 
                                !!eventConfiguration?.alertError && (
                                    <BasicAlert
                                        description={<Markdown.Preview>{eventConfiguration.alertError}</Markdown.Preview>}
                                        severity="error"
                                        icon={faExclamationTriangle}
                                    />
                                )
                            }
                            { 
                                !!eventConfiguration?.alertSuccess && (
                                    <BasicAlert
                                        description={<Markdown.Preview>{eventConfiguration.alertSuccess}</Markdown.Preview>}
                                        severity="success"
                                        icon={faCheckCircle}
                                    />
                                )
                            }
                            {children}
                        </>
                    )
                }
            </Content>
        </>
    );
}

export default SourceDataFormEventTitled;